import { memo } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { CheckCircle2, AlertCircle } from "lucide-react";
import DocumentPreview from "./DocumentPreview";

const DocumentsSection = memo(({ documents, onPreview, title }) => {
  if (!documents || Object.keys(documents).length === 0) {
    return (
      <Card
        sx={{ mb: 3, backgroundColor: "#fef2f2", border: "1px solid #fecaca" }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <AlertCircle
              style={{
                fontSize: "20px",
                color: "#dc2626",
                flexShrink: 0,
                marginTop: "2px",
              }}
            />
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#dc2626",
                }}
              >
                No documents available
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#991b1b",
                  display: "block",
                  mt: 0.5,
                }}
              >
                No documents have been uploaded yet.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const getTotalFiles = () => {
    let count = 0;
    Object.values(documents).forEach((doc) => {
      if (doc.url) count++;
      if (doc.frontUrl) count++;
      if (doc.backUrl) count++;
      if (Array.isArray(doc.urls)) count += doc.urls.length;
      if (doc.file) count++;
      if (doc.front) count++;
      if (doc.back) count++;
      if (Array.isArray(doc.multiple)) count += doc.multiple.length;
    });
    return count;
  };

  const totalFiles = getTotalFiles();

  return (
    <Card
      sx={{ mb: 3, border: "1px solid #e5e7eb", backgroundColor: "#f0fdf4" }}
    >
      <CardContent>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: "#059669",
            mb: 2.5,
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircle2 style={{ fontSize: "20px", color: "#059669" }} />
          {title || "Documents"} ({totalFiles})
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {Object.entries(documents).map(([docKey, docValue]) => {
            const hasFiles =
              docValue.url ||
              docValue.frontUrl ||
              docValue.backUrl ||
              (Array.isArray(docValue.urls) && docValue.urls.length > 0) ||
              docValue.file ||
              docValue.front ||
              docValue.back ||
              (Array.isArray(docValue.multiple) &&
                docValue.multiple.length > 0);

            if (!hasFiles) return null;

            return (
              <Box key={docKey}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 600,
                    color: "#6b7280",
                    mb: 1,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                  }}
                >
                  {docKey.replace(/-/g, " ")}
                </Typography>

                {docValue.url && (
                  <DocumentPreview
                    file={docValue.url}
                    fileName={docValue.url.split("/").pop()}
                    onPreview={onPreview}
                  />
                )}

                {docValue.file && (
                  <DocumentPreview
                    file={docValue.file}
                    fileName={docValue.file.name}
                    onPreview={onPreview}
                  />
                )}

                {docValue.frontUrl && (
                  <DocumentPreview
                    file={docValue.frontUrl}
                    fileName={`${docKey}-front`}
                    onPreview={onPreview}
                  />
                )}

                {docValue.backUrl && (
                  <DocumentPreview
                    file={docValue.backUrl}
                    fileName={`${docKey}-back`}
                    onPreview={onPreview}
                  />
                )}

                {docValue.front && (
                  <DocumentPreview
                    file={docValue.front}
                    fileName={docValue.front.name}
                    onPreview={onPreview}
                  />
                )}

                {docValue.back && (
                  <DocumentPreview
                    file={docValue.back}
                    fileName={docValue.back.name}
                    onPreview={onPreview}
                  />
                )}

                {docValue.urls &&
                  Array.isArray(docValue.urls) &&
                  docValue.urls.map((url, idx) => (
                    <DocumentPreview
                      key={`${docKey}-${idx}`}
                      file={url}
                      fileName={url.split("/").pop()}
                      onPreview={onPreview}
                    />
                  ))}

                {docValue.multiple &&
                  Array.isArray(docValue.multiple) &&
                  docValue.multiple.map((file, idx) => (
                    <DocumentPreview
                      key={`${docKey}-${idx}`}
                      file={file}
                      fileName={file.name}
                      onPreview={onPreview}
                    />
                  ))}
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
});

export default DocumentsSection;
